import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Crown, Loader2, AlertTriangle, Gift } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | error
  const tries = useRef(0);
  const sessionId = params.get("session_id");
  const isGift = params.get("gift") === "1";

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch (_) {}
      tries.current += 1;
      if (tries.current >= 12) { setState("error"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-xl mx-auto px-6 sm:px-8 pt-20 pb-24 text-center" data-testid="payment-success-page">
      {state === "checking" && (
        <>
          <Loader2 className="w-9 h-9 text-[#2C2A29] animate-spin mx-auto" />
          <h1 className="font-heading text-2xl sm:text-3xl text-[#2C2A29] mt-6">{isGift ? "Confirming your gift…" : "Confirming your subscription…"}</h1>
          <p className="font-body text-sm text-[#7A756E] mt-2">This usually takes just a moment.</p>
        </>
      )}
      {state === "paid" && isGift && (
        <div data-testid="gift-confirmed">
          <Gift className="w-10 h-10 text-[#9B7E4E] mx-auto" strokeWidth={1.5} />
          <h1 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] mt-6">Your gift has been sent</h1>
          <p className="font-decree text-lg text-[#4A4744] mt-4">
            Premium is unlocked for them right now, and your blessing has been posted to the Newsfeed.
          </p>
          <Link to="/feed" data-testid="gift-success-feed"
            className="inline-block mt-8 px-8 py-3.5 bg-[#2C2A29] text-[#FAF9F6] rounded-sm hover:opacity-90 transition-opacity duration-300">
            Back to the Newsfeed
          </Link>
        </div>
      )}
      {state === "paid" && !isGift && (
        <div data-testid="payment-confirmed">
          <Crown className="w-10 h-10 text-[#9B7E4E] mx-auto" strokeWidth={1.5} />
          <h1 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] mt-6">Welcome to Premium</h1>
          <p className="font-decree text-lg text-[#4A4744] mt-4">
            Everything is unlocked — unlimited decrees, full plans, every teaching, and unlimited word studies.
          </p>
          <Link to="/generate" data-testid="payment-success-generate"
            className="inline-block mt-8 px-8 py-3.5 bg-[#2C2A29] text-[#FAF9F6] rounded-sm hover:opacity-90 transition-opacity duration-300">
            Start decreeing
          </Link>
        </div>
      )}
      {state === "error" && (
        <div data-testid="payment-error">
          <AlertTriangle className="w-9 h-9 text-[#9B3B3B] mx-auto" strokeWidth={1.5} />
          <h1 className="font-heading text-2xl sm:text-3xl text-[#2C2A29] mt-6">We couldn't confirm the payment</h1>
          <p className="font-body text-sm text-[#7A756E] mt-2">
            If you completed checkout, your access will activate shortly — or try "Restore purchases" on the Premium page.
          </p>
          <Link to="/premium" className="inline-block mt-6 px-6 py-3 border border-[#D9D4CC] text-[#2C2A29] rounded-sm hover:bg-[#EAE6DF] transition-colors duration-300">
            Back to Premium
          </Link>
        </div>
      )}
    </div>
  );
}
